import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  HookParams,
  NotificationTrigger,
  NotificationTriggerListParams,
} from '@/libs/interfaces'
import { notificationTriggersApi } from '@/services/api'

export function useNotificationTriggers({
  params,
  enabled,
}: HookParams<NotificationTriggerListParams>) {
  return useQuery<NotificationTrigger[]>({
    queryKey: QUERY_KEYS.notificationTriggers.list(params),
    queryFn: () => notificationTriggersApi.list(params),
    enabled: (enabled ?? true) && !!params.orgID,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useCreateNotificationTrigger() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: notificationTriggersApi.create,
    onSuccess: (result) => {
      toast.success(getCreatedToastMessage(result, t('communication.automations.saved')))
      // Deliberate exception to the usual void-the-invalidation convention:
      // NotificationTriggerRow's create-vs-update branch depends on the
      // refreshed list carrying the new trigger's ID by the time the row's
      // saving/disabled state clears, not just on eventual cache freshness.
      // Returning (not voiding) this promise keeps the mutation "pending"
      // until that refresh lands, closing a double-create race window.
      return queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.notificationTriggers.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('communication.automations.saveFailed')))
    },
  })
}

export function useUpdateNotificationTrigger() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: notificationTriggersApi.update,
    onSuccess: () => {
      toast.success(t('communication.automations.saved'))
      // Deliberate exception to the usual void-the-invalidation convention:
      // kept consistent with useCreateNotificationTrigger above, whose
      // returned (not voided) invalidation promise is load-bearing for
      // correct create-vs-update routing on the row's next save.
      return queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.notificationTriggers.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('communication.automations.saveFailed')))
    },
  })
}

export function useDeleteNotificationTrigger() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: notificationTriggersApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.notificationTriggers.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('communication.automations.saveFailed')))
    },
  })
}
